<template>
  <div v-if="role" class="fleet-dispatcher">
    <MainBackButton />

    <div class="map-wrapper">
      <FleetDispatcherMap ref="map" :workflow="workflow" />

      <div class="top-btns">
        <p class="button is-white is-rounded bullseye" @click="locate">
          <span class="icon" :class="{ 'fa-spin': zooming }" />
        </p>
        <p>
          <span class="button is-white" @click="fitToFleet">
            <span class="icon">
              <img :src="this.icon" />
            </span>
          </span>
        </p>
      </div>
    </div>

    <DispatcherMain />
  </div>

  <div v-else class="notification">
    <i class="fa fa-kiwi-bird" />
    <!-- <p class="title has-text-info">{{ $t("driver.not-allowed") }}</p> -->
    <p class="title has-text-info">
      You must be logged in with a Dispatcher role to view this page.
    </p>
    <a class="button is-success" @click="goHome">{{ $t("go-home") }}</a>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet.locatecontrol";
import "leaflet.awesome-markers";
import "leaflet.marker.slideto";

import MainBackButton from "@/components/MainBackButton";
import DispatcherMain from "@/components/DispatcherMain.vue";
import FleetDispatcherMap from "@/components/FleetDispatcherMap.vue";

import geo from "@/geo";
import vehicles from "@/vehicles";

const CachedTileLayer =
  require("@yaga/leaflet-cached-tile-layer").CachedTileLayer;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "FleetDispatcher",
  components: {
    MainBackButton,
    DispatcherMain,
    FleetDispatcherMap,
  },
  data() {
    return {
      mapID: "tuktuk-dispatcher-map",
      imgDefault: "/img/transport/siilor.png",
      map: undefined,
      interval: null,
      stateLayer: null,
      locateControl: undefined,
      locateToggle: false, //true,
      slideOpts: { duration: 10000, keepAtCenter: false },
      popupNarrow: { closeButton: false, maxWidth: 80, autoPan: false }, // popup options
      popupWide: { closeButton: false, maxWidth: 300, autoPan: false }, // not working
      dashLine: {
        color: "blue",
        weight: "1",
        dashArray: "5, 5",
        lineCap: "square",
      },
      driverMarker: null,
      driversMarkers: [], // drivers for observers, all active drivers for workflow
      passengerMarkers: [],
      pickupMarker: undefined,
      dropoffMarker: undefined,
      userMarker: null, // tiny invisible dot for stateLayer to help fitTo
      driverLine: undefined,
      zooming: false,
    };
  },
  computed: {
    role() {
      return this.$store.getters.isDispatcher;
    },
    workflow() {
      return this.role.workflow;
    },
    icon() {
      if (!this.workflow) return null;
      var iconName = this.workflow.data.icon || this.workflow.name;
      return vehicles.img(iconName);
    },
    mapState() {
      return this.$store.state.mapState;
    },
  },
  created() {
    L.AwesomeMarkers.Icon.prototype.options.prefix = "fa";
  },
  mounted() {
    // this.updateLocation();
    // this.initMap();
    // this.setupMapState();
    // this.subscribeToStore();
    // this.onVisibilityChange();
    // EventBus.$on("FIT_MAP", () => this.fitToLayer(this.stateLayer));
    // EventBus.$on("UPDATE_IOS_LOCATION", (location) => {
    //   this.map.fire("locationfound", { latlng: location });
    // });
  },
  activated() {
    // this.updateLocation();
  },
  deactivated() {
    this.$notify({ group: "foo", clean: true });
  },
  beforeDestroy() {
    if (this.interval) clearInterval(this.interval);
  },
  methods: {
    fitToFleet() {
      this.$refs.map.updateDrivers(); // this should fit to fleet also
    },
    onVisibilityChange() {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") return;
        // this.updateOrdersFleetDispatcher(); // is this necessary?
        this.updateLocation();
        this.fitToFleet();
      });
    },

    resetMapObjects() {
      this.driverMarker = null;
      this.userMarker = null;
      this.passengerMarkers = [];
    },

    setupMapState() {
      if (this.interval) clearInterval(this.interval); // clear vehicle observer interval
      this.clearLayer(this.stateLayer);
      this.resetMapObjects();
      if (this.driverWorkflow) this.setupMapStateDriver();
      this.drawUserMarker(); // small marker to keep user location in stateLayer
      this.fitToLayer(this.stateLayer, 80);
    },

    setupMapStateDriver() {
      console.log("setup", this.$store.state.selectedTransport);
      this.setupLocateControl(false);
      this.drawPassengersForDriver();
      // this.drawDriverForDriver();
      this.showDriversForObserver(); // show drivers their team
      // this.drawStopsForDriver();
    },

    subscribeToStore() {
      this.$store.subscribe((mutation) => {
        var mapMutations = ["selectTransport", "selectTukTuk", "clearTukTuk"];
        if (mapMutations.includes(mutation.type)) {
          this.setupMapState();
        }
        if (mutation.type === "setTransactionsForWorker") {
          this.console("update wt", this.$store.getters.getWorkerTransactions);
          this.drawPassengersForDriver();
          if (
            this.$store.getters.getWorkerTransactionsStateConfirmed.length >
            0 &&
            !this.isWorkingDispatcher()
          ) {
            this.removeDriversForObserver(); // don't remove if dispatcher
          } else this.showDriversForObserver();
        }
      });
    },

    fitToLayer(layer) {
      this.console("fitToLayer", layer);
      if (!layer) {
        this.zooming = false;
        return;
      }
      if (!layer.getLayers().length) {
        this.zooming = false;
        return;
      }
      var m = 50; // old default, wrong is pixels not meters
      try {
        var userLoc = this.$store.state.userState; //this.$store.getters.getUserLocation;
        var boundsCenter = layer.getBounds().getCenter();
        var dist = geo.distanceToUserInMeters(boundsCenter, userLoc);
        console.log(`flyTo layer ${dist}m`, boundsCenter, userLoc);
        // if (this.$store.state.userState.distance > 100) {
        if (dist > 1000) {
          // this.map.panTo(this.$store.getters.getUserLocation);
          // animate: false
          this.map.fitBounds(layer.getBounds(), { padding: [m, m] });
          this.zooming = false;
        } else {
          // this.map.flyTo(this.$store.getters.getUserLocation);
          this.map.flyToBounds(layer.getBounds(), { padding: [m, m] });
        }
      } catch (err) {
        console.error("fitToLayer error", err);
      }
    },
    clearLayer(layer) {
      if (layer) layer.clearLayers();
    },
    /* toggle between flying to your location and fitting to stateLayer */
    locate() {
      this.$refs.map.recenter();
      this.pollLocation();
      this.zooming = true;
      setTimeout(() => (this.zooming = false), 600);
      // if (this.zooming) return; // don't do anything if already zooming
      // this.zooming = true;
      // this.map.on("zoomend", () => (this.zooming = false));
      // // this.map.on("moveend", () => (this.zooming = false)); // probably need this
      // if (this.locateToggle) {
      //   this.console("flyTo userLocation");
      //   // distance check here should be current map center to userLocation!!!
      //   if (this.$store.state.userState.distance > 100) {
      //     this.map.setView(this.$store.getters.getUserLocation);
      //     this.zooming = false;
      //   } else {
      //     this.map.flyTo(this.$store.getters.getUserLocation);
      //   }
      // } else {
      //   this.console("fit to state layer");
      //   this.fitToLayer(this.stateLayer);
      // }
      // this.locateToggle = !this.locateToggle; // toggle it
      // setTimeout(() => this.map.invalidateSize(), 1000); // might help force redraw
    },

    /////////////////////////////////// The meaty bits ///////////////////////////////////////
    initialiseDriverForPassenger(coords) {
      this.console("initialiseDriverForPassenger");
      var icon = L.divIcon({
        html: `<div class='driver-circle'><img class='driver-big' src='${this.imgDefault}' /></div>`,
        popupAnchor: [-5, -22],
        className: "none",
      });
      this.driverMarker = L.marker(coords, { icon: icon }).addTo(
        this.stateLayer
      );
    },

    removeDriversForObserver() {
      if (this.driversMarkers && this.driversMarkers.length > 0) {
        this.driversMarkers.forEach((m) => this.stateLayer.removeLayer(m)); // first remove previous markers
      }
    },

    drawDriversForObserver(drivers) {
      if (!drivers || drivers.length === 0) return;
      this.removeDriversForObserver();
      var availableIcon = L.divIcon({
        html: `<div class='driver-circle available-bg'><img class='driver-small' src='${this.imgDefault}' /></div>`,
        popupAnchor: [-5, -20],
        className: "none",
      });
      var busyIcon = L.divIcon({
        html: `<div class='driver-circle busy-bg'><img class='driver-small' src='${this.imgDefault}' /></div>`,
        popupAnchor: [-5, -20],
        className: "none",
      });
      var available = { icon: availableIcon, draggable: true };
      var busy = { icon: busyIcon, draggable: true };

      drivers.forEach((ds) => {
        if (ds.id === this.$store.state.user.id) return; // skip yourself
        if (ds.role.role_name === "Dispatcher") return; // skip dispatcher
        if (ds.lat == 0) {
          console.warn("bad driver location!", ds);
          return;
        }
        var t = (new Date() - new Date(ds.updated_at)) / (1000 * 60); // minutes ago
        if (t < 0) return 0;
        if (t > 240) return; // don't show if older than an hour or 4 hours
        var options = available;
        if (ds.role.data.is_busy) options = busy;
        var m = L.marker([ds.lat, ds.lon], options).addTo(this.stateLayer);
        var name = `<strong>${ds.user.name}</strong>`;
        var text = `${name}<br>${Math.round(t)} mins ago`;
        var content = `<a onclick="selectDriver('${ds.id}')">${text}</a>`;
        if (!this.isSuperUser()) content = `<a>${text}</a>`;
        m.bindPopup(content, this.popupNarrow);
        this.driversMarkers.push(m);
      });
    },

    initialiseDriverForDriver() {
      // var html = `<div class='driver-circle cyan-bg'><img class='driver-big' src='${this.imgDefault}' /></div>`;
      var img = this.imgDefault;
      if (
        this.$store.state.selectedTransport.workflow_type_name === "Songthaew"
      ) {
        // html = `<div class='driver-circle cyan-bg'><img class='driver' src='${this.imgSongthaew}' /></div>`;
        img = this.imgSongthaew;
      }
      var driver = L.divIcon({
        html: `<div class='driver-circle cyan-bg'><img class='driver-big' src='${img}' /></div>`,
        popupAnchor: [0, -20],
        className: "none",
      });
      if (this.isWorkingDispatcher()) {
        // change icon for dispatcher
        // html = `<div class='driver-circle dispatcher'><i class="fa fa-phone-volume"></i></div>`;
        driver = L.divIcon({
          html: `<div class='driver-circle dispatcher'><i class="fa fa-phone-volume"></i></div>`,
          popupAnchor: [-5, -20],
          className: "none",
        });
      }
      var icon = { icon: driver };
      var coords = this.$store.getters.getUserLocation;
      this.driverMarker = L.marker(coords, icon).addTo(this.stateLayer);
    },

    showDriversForObserver() {
      if (!this.$store.state.selectedTransport) return;
      var workflowType = this.$store.state.selectedTransport.workflow_type_name;
      if (workflowType === "Public Tuk Tuk") {
        return this.showNearbyDriversForObserver();
      }
      this.$store
        .dispatch("trackDrivers", this.$store.state.selectedTransport.id)
        .then((driverStates) => this.drawDriversForObserver(driverStates))
        .catch((err) => {
          if (!err) return;
          console.warn("trackDrivers", err.message, err.response);
          if (err.response) {
            if (err.response.data === "No active workers") {
              clearInterval(this.interval);
            }
          }
        });
    },

    showNearbyDriversForObserver() {
      this.$store
        .dispatch("trackNearby", this.$store.state.selectedTransport.id)
        .then((driverStates) => this.drawDriversForObserver(driverStates))
        .catch((err) => {
          if (!err) return;
          console.warn("trackNearby", err.message, err.response);
          if (err.response) {
            if (err.response.data === "No active workers") {
              clearInterval(this.interval);
            }
          }
        });
    },

    drawStopsForDriver() {
      var options = {
        radius: 7,
        fillColor: "yellow", // "#28ea3f",//"#0163FF",
        color: "black", //"#0163FF",
        weight: 3,
        opacity: 0.8,
        fillOpacity: 0.7, // className: 'marker-cluster'
      };
      var w = this.$store.state.selectedTransport;
      var stops = w.data.stops;
      stops.forEach((s) => {
        var text = `<i class="fa fa-map-pin"/>&nbsp;<span class="subtitle is-6">${s.name}</span>`;
        L.circleMarker([s.lat, s.lon], options)
          .addTo(this.stateLayer)
          .bindPopup(text, this.popupWide);
      });
    },
    drawPassengerTransaction() {
      var t = this.$store.getters.getCustomerTransactions[0];
      if (!t) return;
      var iconClass = "street-view";
      if (t.data.type === "delivery") iconClass = "store-alt";
      var pickupIcon = L.AwesomeMarkers.icon({
        icon: iconClass, //"arrow-up",
        markerColor: "blue",
        iconColor: "yellow",
      });
      var pickupCoords = [t.data.pickup_stop.lat, t.data.pickup_stop.lon];
      var icon = { icon: pickupIcon };
      var p = L.marker(pickupCoords, icon).addTo(this.stateLayer);
      this.pickupMarker = p;
      var pickupText = this.$t("transport.pickup-point");
      var text = `<strong>${t.data.pickup_stop.name}</strong><br>${pickupText}`;
      p.bindPopup(text, this.popupWide);

      if (t.data.dropoff_stop) {
        var dropoffIcon = L.AwesomeMarkers.icon({
          icon: "flag-checkered", //"arrow-down",
          markerColor: "lightgreen",
          iconColor: "blue",
          extraClasses: "fa-lg",
        });
        var dropoffCoords = [t.data.dropoff_stop.lat, t.data.dropoff_stop.lon];
        icon = { icon: dropoffIcon };
        var d = L.marker(dropoffCoords, icon).addTo(this.stateLayer);
        this.dropoffMarker = d;
        var dropoffText = this.$t("transport.dropoff-point");
        text = `<strong>${t.data.dropoff_stop.name}</strong><br>${dropoffText}`;
        d.bindPopup(text, this.popupWide);
      }

      // draw walking line for passenger
      var latlngs = Array();
      latlngs.push(this.$store.getters.getUserLocation);
      latlngs.push(p.getLatLng());
      var userLine = L.polyline(latlngs, this.dashLine).addTo(this.stateLayer);
      this.pickupMarker.userLine = userLine;
      userLine.pickupCoords = p.getLatLng();
      this.redrawLineForUser(userLine); // update walking line when user location changes
      this.redrawLineOnZoom(userLine);
    },

    redrawLineForUser(line) {
      this.map.on("locationfound", (e) => {
        var userLocation = e.latlng;
        line.setLatLngs([userLocation, line.pickupCoords]);
      });
    },

    drawDriverToPickupLine() {
      var latlngs = Array();
      if (!this.driverLine) {
        if (!(this.pickupMarker && this.driverMarker)) return;
        latlngs.push(this.pickupMarker.getLatLng());
        latlngs.push(this.driverMarker.getLatLng());
        this.driverLine = L.polyline(latlngs, {
          color: "yellow",
          weight: "2",
          dashArray: "5, 10",
        }).addTo(this.stateLayer);
        this.redrawLineOnZoom(this.driverLine);
      } else {
        latlngs.push(this.pickupMarker.getLatLng());
        latlngs.push(this.driverMarker.getLatLng());
        this.driverLine.setLatLngs(latlngs);
      }
    },

    drawPassengerToPickupLine(customerMarker) {
      var c = customerMarker;
      var stop = c.transaction.data.pickup_stop;
      var pickupCoords = [stop.lat, stop.lon];
      var latlngs = Array();
      // this.console("p2p", c.transaction, pickupCoords, c.getLatLng());
      if (!c.line) {
        latlngs.push(c.getLatLng());
        latlngs.push(pickupCoords);
        c.line = L.polyline(latlngs, this.dashLine).addTo(this.stateLayer);
        this.redrawLineOnZoom(c.line);
      } else {
        latlngs.push(c.getLatLng());
        latlngs.push(pickupCoords);
        c.line.setLatLngs(latlngs);
      }
    },

    drawPassengerPickupPoint(customerMarker) {
      var c = customerMarker;
      var s = c.transaction.data.pickup_stop;
      var pickupText = this.$t("transport.pickup-point");
      var text = `<strong>${s.name}</strong><br>${pickupText}`;
      var dot = L.divIcon({
        html: `<div class='pickup-point'></div>`, // yellow dot
        popupAnchor: [5, -10],
        className: "none",
      });
      var icon = { icon: dot };
      var pickup = L.marker([s.lat, s.lon], icon)
        .addTo(this.stateLayer)
        .bindPopup(text, this.popupWide);
      c.pickup = pickup;
    },

    drawPassengerDropoffPoint(customerMarker) {
      var c = customerMarker;
      if (!c.transaction.data.dropoff_stop) return;
      var s = c.transaction.data.dropoff_stop;
      var dropoffText = this.$t("transport.dropoff-point");
      var text = `<strong>${s.name}</strong><br>${dropoffText}`;
      var dot = L.divIcon({
        html: `<div class='dropoff-point'></div>`, // yellow dot
        popupAnchor: [5, -10],
        className: "none",
      });
      var icon = { icon: dot };
      var dropoff = L.marker([s.lat, s.lon], icon)
        .addTo(this.stateLayer)
        .bindPopup(text, this.popupWide);
      c.dropoff = dropoff;
    },

    coloredIcon(state) {
      var color = "blue";
      if (state === "initiated") color = "orange";
      if (state === "confirmed") color = "darkblue";
      if (state === "cancelled_by_passenger") color = "red";
      if (state === "driver_arrived") color = "green";
      var opts = { icon: "street-view", markerColor: color };
      return L.AwesomeMarkers.icon(opts);
    },

    removeOldPassengerTransactionsFromMap() {
      var transactions = this.$store.getters.getWorkerTransactions;
      this.passengerMarkers.forEach((m) => {
        if (!transactions.find((t) => t.id === m.id)) {
          this.stateLayer.removeLayer(m.dropoff);
          this.stateLayer.removeLayer(m.pickup);
          this.stateLayer.removeLayer(m.line);
          this.stateLayer.removeLayer(m);
        }
      });
      // should I reset this.passengerMarkers = []; here?
    },

    driverRejected(t) {
      return this.$store.state.rejectedTransactions.find((rt) => rt === t.id);
    },

    ditherCoords(s) {
      var dither = (Math.random() - 0.5) * 0.0002;
      return [s.lat + dither, s.lon + dither];
    },

    drawPassengersForDriver() {
      console.log(
        "draw passengers for driver",
        this.$store.getters.getWorkerTransactions
      );
      this.removeOldPassengerTransactionsFromMap();
      this.$store.getters.getWorkerTransactions.forEach((t) => {
        // var latLng = [t.customer.user_state.lat, t.customer.user_state.lon];
        var coords = this.ditherCoords(t.data.pickup_stop); // fake passenger location
        if (this.passengerMarkers.find((m) => m.id === t.id)) {
          // if passenger marker exists just update it or remove it
          var m = this.passengerMarkers.find((m) => m.id === t.id);
          // m.setLatLng(coords);
          m.slideTo(coords, this.slideOpts);
          m.setIcon(this.coloredIcon(t.state));
          this.drawPassengerToPickupLine(m);
          // this.console("setIcon", m, this.coloredIcon(t.state));
          var removalStates = ["alighted", "acknowledged"]; // "enroute"
          if (!t.data.dropoff_stop) removalStates.push("enroute");
          if (removalStates.includes(t.state) || this.driverRejected(t)) {
            this.stateLayer.removeLayer(m.pickup);
            this.stateLayer.removeLayer(m.dropoff);
            this.stateLayer.removeLayer(m.line);
            this.stateLayer.removeLayer(m);
          }
          if (t.data.dropoff_stop && t.state === "enroute") {
            if (!m.dropoff) {
              this.stateLayer.removeLayer(m);
              this.stateLayer.removeLayer(m.pickup);
              this.drawPassengerDropoffPoint(m);
              this.fitToLayer(this.stateLayer);
            }
          }
        } else {
          // otherwise need to add the marker
          // if (t.state === "enroute" || t.state === "acknowledged") return;
          var skipStates = ["alighted", "acknowledged"];
          if (!t.data.dropoff_stop) skipStates.push("enroute");
          if (skipStates.includes(t.state)) return;
          var c = L.marker(coords, {
            icon: this.coloredIcon(t.state),
            draggable: true,
          }).addTo(this.stateLayer);
          var profPic = this.profPic(t.customer);
          var img = `<img src=${profPic} style='object-fit: cover; height: 90px; width: 90px; border-radius: 4px' />`;
          var content = `${img}<center><strong>${t.customer.name
            }</strong><br>${this.$t("passenger.passenger")}</center>`;
          var popup = L.popup(this.popupNarrow).setContent(content);
          c.bindPopup(popup);
          c.customer = t.customer;
          c.id = t.id;
          c.transaction = t;
          this.drawPassengerToPickupLine(c);
          this.drawPassengerPickupPoint(c);
          // if (t.state !== "enroute") this.drawPassengerPickupPoint(c);
          // if (t.state === "enroute") {
          //   this.drawPassengerDropoffPoint(c);
          //   // this.stateLayer.removeLayer(m.pickup);
          //   // this.stateLayer.removeLayer(m.line);
          // }
          this.passengerMarkers.push(c);
          this.console("customer marker", c);
        }
      });
      // if (this.passengerMarkers.length > 0) this.removeDriversForObserver();
      // if (this.$store.getters.getWorkerTransactionsStateConfirmed.length > 0)
    },

    drawPrivateTukTuks() {
      var privateTuks = this.$store.getters.getValidWorkflowsPrivateTuk;
      if (!privateTuks) return;
      var bldgIcon = L.AwesomeMarkers.icon({
        icon: "home",
        markerColor: "blue",
      });
      privateTuks.forEach((w) => {
        var b = L.marker([w.business.lat, w.business.lon], { icon: bldgIcon })
          .addTo(this.stateLayer)
          .bindPopup(w.business.name);
        b.workflow = w;
        b.on("click", (e) => {
          if (!this.loginOkay("Login to use Private Tuk Tuk")) return;
          var businessName = e.target.workflow.business.name;
          // this.$store.commit("selectTukTuk", e.target.workflow);
          // this.success(`${this.$t("transport.selected")} ${businessName}`);
          this.$swal({
            title: businessName,
            text: this.$t("transport.select-private-tuk"), //"Select this private tuk tuk?",
            showCancelButton: true,
            confirmButtonText: this.$t("confirm"),
            cancelButtonText: this.$t("cancel"),
          }).then((result) => {
            if (result.value) {
              this.$store.commit("selectTukTuk", e.target.workflow);
            }
          });
        });
      });
    },

    drawTransportStops(w) {
      this.console("draw transport stops", w);
      if (!w.data || !w.data.stops) return;
      var dot = L.divIcon({
        html: `<div class='purple-stop'></div>`,
        popupAnchor: [3, -6],
        className: "none",
      });
      var stops = w.data.stops;
      if (w.workflow_type_name === "Sii Lor") {
        stops = this.$store.getters.getSiilorStopsAll; // siilor show all stops
        dot = L.divIcon({
          html: `<div class='purple-stop siilor-stop'></div>`,
          popupAnchor: [0, -6],
          className: "none",
        });
      }
      if (w.workflow_type_name === "Songthaew") {
        stops = this.$store.getters.getSongthaewStopsAll; // siilor show all stops
        dot = L.divIcon({
          html: `<div class='purple-stop songthaew-stop'></div>`,
          popupAnchor: [0, -6],
          className: "none",
        });
      }
      if (w.workflow_type_name === "Public Tuk Tuk") {
        stops = this.$store.getters.getPublicTuktukStopsAll; // siilor show all stops
        dot = L.divIcon({
          html: `<div class='purple-stop publictuk-stop'></div>`,
          popupAnchor: [0, -6],
          className: "none",
        });
      }
      stops.forEach((s) => {
        var text = `<i class="fa fa-map-marker-alt" style="font-size:1.3em; color:grey"></i>&nbsp;<span class="subtitle is-6">${s.name}</span>`;
        L.marker([s.lat, s.lon], { icon: dot })
          .addTo(this.stateLayer)
          .bindPopup(text, this.popupWide);
      });
    },

    setupLocateControl(showUserLocationDot) {
      if (this.locateControl) this.map.removeControl(this.locateControl);
      var clickBehavior = {
        inView: "setView",
        outOfView: "inView", //"setView",
        inViewNotFollowing: "inView",
      };
      var options = {
        enableHighAccuracy: true,
        keepCurrentZoomLevel: true,
        flyTo: true,
        showPopup: true,
        // drawCircle: false,
        circleStyle: {
          color: "green",
          fillColor: "white",
          fillOpacity: 0.01,
          opacity: 0.01,
        },
        drawMarker: false, // false for driver only
        showCompass: false, // false for driver only
        clickBehavior: clickBehavior,
        onLocationError: (err) => console.warn("locateControl", err),
      };
      if (showUserLocationDot) {
        options.drawMarker = true;
        options.showCompass = true;
      }
      this.locateControl = L.control.locate(options).addTo(this.map);
      this.locateControl.start(); // centers map to user geolocation, throwing an error
      // setTimeout(() => this.locateControl.start(), 1000); // centers map to user geolocation
      var lc = this.$el.querySelector(".leaflet-control-locate");
      lc.style.visibility = "hidden"; // hide the default locate control
    },

    userIsFarAway() {
      var w = this.$store.state.selectedTransport;
      if (!w || !w.data.stops) return true;
      if (
        w.data.stops[0].distance > 1000 ||
        w.data.stops[0].distance == undefined
      ) {
        this.console(`FAR AWAY true: ${w.data.stops[0].distance}m`);
        return true; // if more than 1km from closest stop then isFarAway
      }
      // console.warn("stop distance", w.data.stops[0].distance);
      // this.console("FAR AWAY false");
      return false;
    },

    drawUserMarker() {
      if (!this.$store.getters.getUserLocation) {
        console.warn("drawUserMarker getters.getUserLocation null");
        return;
      }
      if (this.userIsFarAway()) return; // if user is far away don't draw
      var coords = this.$store.getters.getUserLocation;
      var opts = {
        radius: 10,
        stroke: false,
        fillColor: "#ffc0cb",
        fillOpacity: 0.3,
      };
      this.userMarker = L.circle(coords, opts).addTo(this.stateLayer); // draw tiny 1m circle
      var debug = JSON.stringify(this.$store.state.userState);
      this.userMarker.bindPopup(debug, this.popupWide);
      this.console("drawUserMarker", coords, this.userMarker);
      // alert("Added userMarker to stateLayer");
      this.map.on("locationfound", (e) => {
        if (!this.userMarker) return;
        this.userMarker.setLatLng(e.latlng);
        // could redraw walking line for passenger here?
      });
    },
    redrawLineOnZoom(line) {
      this.console("REDRAW LINE", line);
      this.map.on("zoomend", () => line.redraw());
    },
    initMap() {
      var tilesURL = this.$t("geo.map-tiles"); // language specific maps
      var initialLocation = [13.73114, 100.57529]; // default: Bangkok, Soi 41
      if (this.$store.getters.getUserLocation) {
        initialLocation = this.$store.getters.getUserLocation;
      }
      // attribution: '<a href="https://www.maptiler.com/copyright/" target="_blank">© MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>',

      var mapOptions = {
        attributionControl: false,
        doubleClickZoom: true, // this is default anyway, but not firing on mobile safari
      };
      this.map = L.map(this.mapID, mapOptions).setView(initialLocation, 17); // set zoom level
      this.map.removeControl(this.map.zoomControl);
      // L.tileLayer(tilesURL, {
      new CachedTileLayer(tilesURL, {
        tileSize: 512,
        zoomOffset: -1,
        minZoom: 2,
        maxZoom: 19,
        crossOrigin: true,
        maxAge: 1000 * 60 * 60 * 24 * 30, // optional, default is 7 days
      }).addTo(this.map);
      this.stateLayer = L.featureGroup().addTo(this.map);
    },
  },
};
</script>

<style lang="scss">
#tuktuk-dispatcher-map {

  /* see app.vue for global leaflet styles */
  .purple-stop {
    height: 18px;
    width: 18px;
    border-radius: 50%;
    filter: drop-shadow(2px 2px 2px #555);
    background-color: magenta;
    opacity: 0.9;
    border-style: solid;
    border-width: 1px;
    border-color: darkslategrey;
    /* animation: shadow-pulse 1s 999; */
  }

  .siilor-stop {
    height: 12px;
    width: 12px;
    opacity: 0.8;
    background-color: lightgreen;
  }

  .songthaew-stop {
    height: 12px;
    width: 12px;
    opacity: 0.8;
    background-color: red;
  }

  .publictuk-stop {
    height: 12px;
    width: 12px;
    opacity: 0.8;
    background-color: cyan;
  }

  /* .test-icon { content: url("../assets/bullseye.png"); } */
  .dropoff-point {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    /* transform: translateX(-50%) translateY(-50%); */
    filter: drop-shadow(2px 2px 2px #555);
    background-color: rgb(0, 89, 255);
    border-style: solid;
    border-width: 0.5px;
    border-color: grey;
    animation: shadow-pulse 1s 999;
  }

  .pickup-point {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    /* transform: translateX(-50%) translateY(-50%); */
    filter: drop-shadow(2px 2px 2px #555);
    background-color: lightgreen;
    border-style: solid;
    border-width: 0.5px;
    border-color: grey;
    animation: shadow-pulse 1s 999;
  }

  @keyframes shadow-pulse {
    0% {
      box-shadow: 0 0 0 0px rgba(247, 211, 8, 0.95);
    }

    100% {
      box-shadow: 0 0 0 10px rgba(247, 211, 8, 0);
    }
  }

  .driver-circle {
    /* for passenger */
    height: 43px;
    width: 43px;
    border-radius: 50%;
    padding: 4px;
    transform: translateX(-50%) translateY(-50%);
    filter: drop-shadow(2px 2px 2px #555);
    background-color: rgba(255, 255, 0, 0.651);
    border-style: solid;
    border-width: 0.5px;
    border-color: grey;
    animation: shadow-pulse 1s 999;
  }

  .cyan-bg {
    /* for driver */
    background-color: cyan;
  }

  .dispatcher {
    /* for driver */
    height: 26px;
    width: 26px;
    background-color: cyan;
  }

  .opaque-bg {
    height: 30px;
    width: 30px;
    background-color: rgba(255, 255, 255, 0.7);
    border-width: 0px;
  }

  .available-bg {
    height: 30px;
    width: 30px;
    background-color: rgba(159, 255, 155, 0.7);
    border-width: 0px;
  }

  .busy-bg {
    height: 30px;
    width: 30px;
    background-color: rgba(255, 94, 201, 0.7);
    border-width: 0px;
  }

  .driver-small {
    height: 20px;
    width: 20px;
    filter: drop-shadow(1px 1px 1px #555);
  }

  .driver-big {
    height: 30px;
    width: 30px;
    filter: drop-shadow(1px 1px 1px #555);
  }

  .none {
    /* empty on purpose to hide small white box under divIcon */
  }

  .leaflet-popup-content {
    padding: 5px 5px;
    margin: 1px 0;
  }
}
</style>

<style scoped>
.map-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
}

.top-btns {
  position: absolute;
  right: 10px;
  top: 10px;
  opacity: 0.98;
  -webkit-filter: drop-shadow(2px 2px 2px #666);
  filter: drop-shadow(2px 2px 2px #666);
}

.top-btns .button {
  /* padding: 9px; */
  width: 36px;
  height: 36px;
}

.bullseye {
  display: block;
  margin-bottom: 10px;
  padding: 12px;
}

.bullseye span {
  margin-top: -7px;
  content: url("../assets/bullseye.png");
}

.fa-kiwi-bird {
  margin-bottom: 10px;
  font-size: 5em;
  color: rgb(255, 133, 154);
}
</style>